import { useRef, useState } from 'react';

import {
    type FieldPath,
    type FieldValues,
    useEvent,
    useFormContext,
    useFormWatch
} from '@american-autonomy/ag-ui';
import type { FormPathValue } from '@american-autonomy/ag-ui/dist/hooks/useForm/types';

import ClientSelectPanelField from 'components/fields/ClientSelectPanelField';
import FarmSelectPanelField from 'components/fields/FarmSelectPanelField';
import FieldSelectPanelField from 'components/fields/FieldSelectPanelField';
import {
    CLIENT_FIELD_NAME,
    FARM_FIELD_NAME,
    FIELD_NAME_FIELD_NAME,
    FIELD_UPDATE_OPTIONS
} from 'components/fields/SelectClientFarmFieldField/constants';
import type {
    ClientFarmFieldSchema,
    Panel
} from 'components/fields/SelectClientFarmFieldField/types';

type UseDataProps<T extends FieldValues = ClientFarmFieldSchema> = {
    namePrefix?: FieldPath<T>;
};

// Each selection narrows the ones below it, so changing or clearing one invalidates its dependents.
const DEPENDENT_FIELDS: Record<NonNullable<Panel>, (keyof ClientFarmFieldSchema)[]> = {
    [CLIENT_FIELD_NAME]: [FARM_FIELD_NAME, FIELD_NAME_FIELD_NAME],
    [FARM_FIELD_NAME]: [FIELD_NAME_FIELD_NAME],
    [FIELD_NAME_FIELD_NAME]: []
};

const useData = <T extends FieldValues = ClientFarmFieldSchema>({
    namePrefix
}: UseDataProps<T>) => {
    const { control, setValue } = useFormContext<T>();

    const fieldName = (name: keyof ClientFarmFieldSchema) =>
        (namePrefix ? `${namePrefix}.${name}` : String(name)) as FieldPath<T>;

    const [openPanel, setOpenPanel] = useState<Panel>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const clientFormValue = useFormWatch({
        control,
        name: fieldName(CLIENT_FIELD_NAME)
    });

    const farmFormValue = useFormWatch({
        control,
        name: fieldName(FARM_FIELD_NAME)
    });

    const fieldFormValue = useFormWatch({
        control,
        name: fieldName(FIELD_NAME_FIELD_NAME)
    });

    const panels = {
        [CLIENT_FIELD_NAME]: {
            component: ClientSelectPanelField,
            value: clientFormValue
        },
        [FARM_FIELD_NAME]: {
            component: FarmSelectPanelField,
            value: farmFormValue
        },
        [FIELD_NAME_FIELD_NAME]: {
            component: FieldSelectPanelField,
            value: fieldFormValue
        }
    };

    const hasIncompleteSelection = !!clientFormValue && (!farmFormValue || !fieldFormValue);

    const clearField = (name: keyof ClientFarmFieldSchema) => {
        setValue(fieldName(name), null as FormPathValue<T, FieldPath<T>>, FIELD_UPDATE_OPTIONS);
    };

    // Clearing dependents when a parent changes is owned by SelectFarmField and SelectFieldField,
    // which watch the clientId and farmId they are handed. That cascades naturally and covers every
    // path, including the pills' remove buttons.

    const handlePanelOpen = (panel: Panel) => {
        setOpenPanel(panel);
    };

    const handleChange = (panel: Panel, value: { label: string; value: string }) => {
        if (!panel) {
            return;
        }

        setValue(fieldName(panel), value as FormPathValue<T, FieldPath<T>>, FIELD_UPDATE_OPTIONS);

        DEPENDENT_FIELDS[panel]?.forEach(field => clearField(field));
    };

    const handlePanelClose = (selectedValue?: { label: string; value: string }) => {
        if (!selectedValue || !openPanel) {
            setOpenPanel(null);

            return;
        }

        const nextPanelMap: Record<NonNullable<Panel>, Panel> = {
            [CLIENT_FIELD_NAME]: FARM_FIELD_NAME,
            [FARM_FIELD_NAME]: !fieldFormValue ? FIELD_NAME_FIELD_NAME : null,
            [FIELD_NAME_FIELD_NAME]: null
        };

        setOpenPanel(nextPanelMap[openPanel] ?? null);
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;

        if (!containerRef.current?.contains(target) && openPanel) {
            setOpenPanel(null);
        }
    };

    useEvent({ event: 'mouseup', handler: handleClickOutside });

    return {
        clientFormValue,
        containerRef,
        control,
        farmFormValue,
        fieldFormValue,
        fieldName,
        handleChange,
        handlePanelClose,
        handlePanelOpen,
        hasIncompleteSelection,
        openPanel,
        panels
    };
};

export default useData;
